import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
var _excluded = ["label", "tooltipText"];
import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { default as MuiTreeItem } from '@mui/lab/TreeItem';
import Tooltip from '../../Tooltip';
import { default as withStyles } from '../../styles';
import { spacing30, spacing40, boxShadowFocusInset } from '../../styles/tokens';
import useTextOverflow from './useTextOverflow';
import { NODE_LABEL_CLASS } from '../MasterDetail';
import { elementTypeAcceptingRef } from '@mui/utils';
var styles = function styles(theme) {
  return {
    root: {
      '&$selected > $content $label:hover, &$selected:focus > $content $label': {
        backgroundColor: 'unset'
      },
      '& > $content:hover, &$selected > $content:hover, &$selected:focus > $content': {
        backgroundColor: theme.palette.grey[250]
      }
    },
    content: {
      'margin': 0,
      'outline': 0,
      'padding': 0,
      '&$selected': {
        backgroundColor: 'unset'
      },
      '& > $label': {
        paddingLeft: '0',
        minWidth: 'unset'
      },
      '&$focused': {
        backgroundColor: theme.palette.grey[250],
        boxShadow: boxShadowFocusInset
      },
      '&$selected$focused': {
        backgroundColor: theme.palette.grey[250]
      },
      '& > $iconContainer': {
        marginRight: spacing40,
        width: '0.125rem' // Don't have size token for 2px
      }
    },

    label: {
      padding: spacing30 + " " + spacing30 + " " + spacing30 + " 0"
    },
    expanded: {},
    selected: {},
    group: {},
    focused: {},
    iconContainer: {}
  };
};
var TreeItem = function TreeItem(props) {
  var treeItemRef = useRef(null);
  var _useTextOverflow = useTextOverflow(treeItemRef, "." + NODE_LABEL_CLASS),
    overflow = _useTextOverflow[0];
  var label = props.label,
    tooltipText = props.tooltipText,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  var newLabel = /*#__PURE__*/React.createElement(React.Fragment, null, overflow ? /*#__PURE__*/React.createElement(Tooltip, {
    title: tooltipText
  }, label) : label);
  return /*#__PURE__*/React.createElement(MuiTreeItem, _extends({
    ref: treeItemRef,
    label: newLabel
  }, rest));
};
TreeItem.propTypes = {
  /**
   * The content of the component.
   */
  children: PropTypes.node,
  /**
   * Override or extend the styles applied to the component.
   */
  classes: PropTypes.object,
  /**
   * @ignore
   */
  className: PropTypes.string,
  /**
   * The icon used to collapse the node.
   */
  collapseIcon: PropTypes.node,
  /**
   * The component used for the content node.
   */
  ContentComponent: elementTypeAcceptingRef,
  /**
   * Props applied to ContentComponent
   */
  ContentProps: PropTypes.object,
  /**
   * If `true`, the node is disabled.
   */
  disabled: PropTypes.bool,
  /**
   * The icon displayed next to a end node.
   */
  endIcon: PropTypes.node,
  /**
   * The icon used to expand the node.
   */
  expandIcon: PropTypes.node,
  /**
   * The icon to display next to the tree node's label.
   */
  icon: PropTypes.node,
  /**
   * The tree node label.
   */
  label: PropTypes.node,
  /**
   * The id of the node.
   */
  nodeId: PropTypes.string.isRequired,
  /**
   * @ignore
   */
  onClick: PropTypes.func,
  /**
   * @ignore
   */
  onFocus: PropTypes.func,
  /**
   * @ignore
   */
  onKeyDown: PropTypes.func,
  /**
   * @ignore
   */
  onMouseDown: PropTypes.func,
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])), PropTypes.func, PropTypes.object]),
  /**
   * The component used for the transition.
   */
  TransitionComponent: PropTypes.elementType,
  /**
   * Props applied to the [`Transition`](http://reactcommunity.org/react-transition-group/transition#Transition-props) element.
   */
  TransitionProps: PropTypes.object,
  /**
   * @ignore
   */
  tooltipText: PropTypes.string
};
TreeItem.muiName = 'TreeItem';
TreeItem.displayName = 'TreeItem';
export default withStyles(styles)(TreeItem);